// frontend/src/pages/ExamCountdown/ExamCountdown.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Chip, LinearProgress,
  CircularProgress, Alert, TextField, Select, MenuItem, FormControl,
  InputLabel, OutlinedInput, IconButton, Collapse, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider
} from '@mui/material';
import {
  AccessAlarm, CheckCircle, Add, Delete, ExpandMore, ExpandLess,
  CalendarToday, TrendingUp, Warning, FlagCircle
} from '@mui/icons-material';
import FeatureGate from '../../components/subscription/FeatureGate';
import UpgradePrompt from '../../components/subscription/UpgradePrompt';
import api from '../../services/api';

const readinessColors = { ready: '#4caf50', almost_ready: '#ff9800', needs_work: '#f44336', not_ready: '#9e9e9e' };
const phaseColors = { foundation: '#2196f3', consolidation: '#ff9800', intensive: '#f44336', final_push: '#4caf50' };

function CreateCountdownDialog({ open, onClose, onCreate, topics, loading }) {
  const [examName, setExamName] = useState('');
  const [examDate, setExamDate] = useState('');
  const [examType, setExamType] = useState('mid_year');
  const [selectedTopics, setSelectedTopics] = useState([]);

  const handleCreate = () => {
    if (!examName || !examDate || selectedTopics.length === 0) return;
    onCreate({ examName, examDate, examType, topicsCovered: selectedTopics });
  };

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Exam Countdown</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <TextField label="Exam Name" value={examName} onChange={e => setExamName(e.target.value)}
          placeholder="e.g. Mid-Year Maths Exam" fullWidth size="small" sx={{ mt: 1 }} />
        <TextField label="Exam Date" type="date" value={examDate}
          onChange={e => setExamDate(e.target.value)} InputLabelProps={{ shrink: true }}
          inputProps={{ min: minDateStr }} fullWidth size="small" />
        <FormControl fullWidth size="small">
          <InputLabel>Exam Type</InputLabel>
          <Select value={examType} onChange={e => setExamType(e.target.value)} label="Exam Type">
            <MenuItem value="mid_year">Mid-Year Exam</MenuItem>
            <MenuItem value="trial">Trial / Prelim</MenuItem>
            <MenuItem value="final">Final Exam</MenuItem>
            <MenuItem value="topic_test">Topic Test</MenuItem>
            <MenuItem value="class_test">Class Test</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth size="small">
          <InputLabel>Topics Covered</InputLabel>
          <Select multiple value={selectedTopics}
            onChange={e => setSelectedTopics(e.target.value)}
            input={<OutlinedInput label="Topics Covered" />}
            renderValue={sel => (<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {sel.map(t => <Chip key={t} label={t} size="small" />)}
            </Box>)}>
            {topics.map(t => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
          </Select>
        </FormControl>
        <Button variant="text" size="small" onClick={() => setSelectedTopics([...topics])}>Select All Topics</Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate}
          disabled={loading || !examName || !examDate || selectedTopics.length === 0}
          startIcon={loading ? <CircularProgress size={16} /> : <Add />}>
          Create Countdown
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CountdownCard({ countdown, onSelect, onDelete }) {
  const daysLeft = Math.max(0, Math.ceil((new Date(countdown.examDate) - new Date()) / 86400000));
  const currentPhase = countdown.phases.find(p => {
    const today = countdown.dailyPlan.find(d => {
      const dDate = new Date(d.date).toDateString();
      return dDate === new Date().toDateString();
    });
    return today && today.phase === p.name;
  }) || countdown.phases[countdown.phases.length - 1];

  return (
    <Card sx={{ mb: 2, cursor: 'pointer', '&:hover': { boxShadow: 3 } }} onClick={() => onSelect(countdown)}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{countdown.examName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(countdown.examDate).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: daysLeft <= 7 ? '#f44336' : daysLeft <= 14 ? '#ff9800' : '#2196f3' }}>
              {daysLeft}
            </Typography>
            <Typography variant="caption" color="text.secondary">days left</Typography>
          </Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip label={currentPhase ? currentPhase.label : 'Preparing'} size="small"
            sx={{ bgcolor: phaseColors[currentPhase?.name] || '#9e9e9e', color: '#fff' }} />
          <Chip label={`${countdown.overallReadiness}% ready`} size="small" variant="outlined"
            color={countdown.overallReadiness >= 70 ? 'success' : countdown.overallReadiness >= 40 ? 'warning' : 'error'} />
        </Box>

        <LinearProgress variant="determinate" value={countdown.overallReadiness}
          sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
          color={countdown.overallReadiness >= 70 ? 'success' : countdown.overallReadiness >= 40 ? 'warning' : 'error'} />

        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {countdown.topicReadiness.slice(0, 6).map(tr => (
            <Chip key={tr.topic} label={tr.topic} size="small"
              sx={{ fontSize: '0.65rem', borderColor: readinessColors[tr.readinessLevel], color: readinessColors[tr.readinessLevel] }}
              variant="outlined" />
          ))}
          {countdown.topicReadiness.length > 6 && (
            <Chip label={`+${countdown.topicReadiness.length - 6} more`} size="small" variant="outlined" />
          )}
        </Box>

        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            {countdown.completion.completedDays}/{countdown.completion.totalDays} days completed
          </Typography>
          <IconButton size="small" onClick={e => { e.stopPropagation(); onDelete(countdown._id); }}
            sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
}

function CountdownDetail({ countdown, onBack, onCompleteDay }) {
  const [expandedDay, setExpandedDay] = useState(null);
  const [loading, setLoading] = useState(false);

  const todayStr = new Date().toDateString();
  const todayEntry = countdown.dailyPlan.find(d => new Date(d.date).toDateString() === todayStr);
  const daysLeft = Math.max(0, Math.ceil((new Date(countdown.examDate) - new Date()) / 86400000));

  const handleComplete = async (dayNumber) => {
    setLoading(true);
    await onCompleteDay(countdown._id, dayNumber);
    setLoading(false);
  };

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Button variant="text" onClick={onBack} sx={{ mb: 1 }}>&larr; Back to countdowns</Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>{countdown.examName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(countdown.examDate).toLocaleDateString('en-ZA', { weekday: 'long', day: 'numeric', month: 'long' })}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'center', p: 1.5, borderRadius: 2, bgcolor: daysLeft <= 7 ? '#ffebee' : '#e3f2fd' }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: daysLeft <= 7 ? '#f44336' : '#2196f3' }}>{daysLeft}</Typography>
          <Typography variant="caption">days</Typography>
        </Box>
      </Box>

      {/* Readiness */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2">Exam Readiness</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{countdown.overallReadiness}%</Typography>
          </Box>
          <LinearProgress variant="determinate" value={countdown.overallReadiness} sx={{ height: 8, borderRadius: 4, mb: 1.5 }}
            color={countdown.overallReadiness >= 70 ? 'success' : countdown.overallReadiness >= 40 ? 'warning' : 'error'} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {countdown.topicReadiness.map(tr => (
              <Chip key={tr.topic} size="small"
                label={`${tr.topic}${tr.accuracy != null ? ` ${tr.accuracy}%` : ''}`}
                icon={tr.readinessLevel === 'ready' ? <CheckCircle /> : tr.readinessLevel === 'not_ready' ? <Warning /> : <TrendingUp />}
                sx={{ borderColor: readinessColors[tr.readinessLevel], color: readinessColors[tr.readinessLevel],
                  '& .MuiChip-icon': { color: readinessColors[tr.readinessLevel] } }}
                variant="outlined" />
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Today's task */}
      {todayEntry && todayEntry.status !== 'completed' && (
        <Card sx={{ mb: 2, border: '2px solid #2196f3' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FlagCircle sx={{ color: '#2196f3' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Today&apos;s Plan</Typography>
              <Chip label={todayEntry.phaseLabel} size="small" sx={{ bgcolor: phaseColors[todayEntry.phase], color: '#fff' }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{todayEntry.description}</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip label={`${todayEntry.questionCount} questions`} size="small" variant="outlined" />
              <Chip label={`~${todayEntry.estimatedMinutes} min`} size="small" variant="outlined" />
              <Chip label={todayEntry.topics.join(', ')} size="small" variant="outlined" />
            </Box>
            <Button variant="contained" size="small" sx={{ mt: 1.5 }}
              onClick={() => handleComplete(todayEntry.dayNumber)} disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <CheckCircle />}>
              Mark Today Complete
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Phase overview */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Phases</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {countdown.phases.map(p => (
          <Chip key={p.name} label={`${p.label} (Days ${p.startDay}-${p.endDay})`} size="small"
            sx={{ bgcolor: phaseColors[p.name], color: '#fff' }} />
        ))}
      </Box>

      {/* Daily plan */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Daily Plan</Typography>
      {countdown.dailyPlan.map(entry => {
        const isToday = new Date(entry.date).toDateString() === todayStr;
        const isPast = new Date(entry.date) < new Date() && !isToday;
        const isExpanded = expandedDay === entry.dayNumber;

        return (
          <Card key={entry.dayNumber} sx={{
            mb: 1, opacity: entry.status === 'completed' ? 0.7 : isPast ? 0.5 : 1,
            borderLeft: `3px solid ${phaseColors[entry.phase]}`,
            ...(isToday ? { border: '2px solid #2196f3' } : {})
          }}>
            <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setExpandedDay(isExpanded ? null : entry.dayNumber)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {entry.status === 'completed' ? <CheckCircle fontSize="small" sx={{ color: '#4caf50' }} /> :
                    isToday ? <FlagCircle fontSize="small" sx={{ color: '#2196f3' }} /> :
                    <CalendarToday fontSize="small" sx={{ color: '#9e9e9e' }} />}
                  <Typography variant="body2" sx={{ fontWeight: isToday ? 600 : 400 }}>
                    Day {entry.dayNumber} — {new Date(entry.date).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip label={entry.topics.join(', ')} size="small" sx={{ fontSize: '0.65rem', maxWidth: 150 }} />
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </Box>
              </Box>
              <Collapse in={isExpanded}>
                <Box sx={{ mt: 1, pl: 4 }}>
                  <Typography variant="body2" color="text.secondary">{entry.description}</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={entry.phaseLabel} size="small" sx={{ bgcolor: phaseColors[entry.phase], color: '#fff', fontSize: '0.65rem' }} />
                    <Chip label={`${entry.questionCount}q`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    <Chip label={`${entry.estimatedMinutes}min`} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    <Chip label={entry.difficulty} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                  </Box>
                  {entry.status !== 'completed' && !isPast && (
                    <Button size="small" variant="outlined" sx={{ mt: 1 }}
                      onClick={() => handleComplete(entry.dayNumber)} disabled={loading}>
                      Mark Complete
                    </Button>
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}

function ExamCountdownContent() {
  const [countdowns, setCountdowns] = useState([]);
  const [selectedCountdown, setSelectedCountdown] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState(null);

  const fetchCountdowns = useCallback(async () => {
    try { setLoading(true); const res = await api.get('/api/exam-countdown/active'); if (res.data.success) setCountdowns(res.data.data); }
    catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  const fetchTopics = useCallback(async () => {
    try { const res = await api.get('/api/study-plan/topics'); if (res.data.success) setTopics(res.data.data.topics); }
    catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchCountdowns(); fetchTopics(); }, [fetchCountdowns, fetchTopics]);

  const handleCreate = async (data) => {
    try {
      setCreating(true); setError(null);
      const res = await api.post('/api/exam-countdown/create', data);
      if (res.data.success) { setCountdowns(prev => [...prev, res.data.data]); setDialogOpen(false); }
    } catch (err) { setError(err.response?.data?.error || 'Failed to create countdown'); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/exam-countdown/${id}`);
      setCountdowns(prev => prev.filter(c => c._id !== id));
      if (selectedCountdown && selectedCountdown._id === id) setSelectedCountdown(null);
    } catch (err) { console.error(err); }
  };

  const handleCompleteDay = async (countdownId, dayNumber) => {
    try {
      const res = await api.patch(`/api/exam-countdown/${countdownId}/day/${dayNumber}/complete`, {});
      if (res.data.success) {
        const updated = await api.get(`/api/exam-countdown/${countdownId}`);
        if (updated.data.success) {
          setSelectedCountdown(updated.data.data);
          setCountdowns(prev => prev.map(c => c._id === countdownId ? updated.data.data : c));
        }
      }
    } catch (err) { console.error(err); }
  };

  if (loading) return (<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>);

  if (selectedCountdown) {
    return <CountdownDetail countdown={selectedCountdown} onBack={() => setSelectedCountdown(null)} onCompleteDay={handleCompleteDay} />;
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>Exam Countdown</Typography>
          <Typography variant="body2" color="text.secondary">Track your preparation day by day</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}
          disabled={countdowns.length >= 5}>New Countdown</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {countdowns.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <AccessAlarm sx={{ fontSize: 48, color: '#9e9e9e', mb: 1 }} />
            <Typography variant="h6" color="text.secondary">No active countdowns</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create an exam countdown to get a personalised day-by-day revision plan.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
              Create Your First Countdown
            </Button>
          </CardContent>
        </Card>
      ) : (
        countdowns.map(c => (
          <CountdownCard key={c._id} countdown={c} onSelect={setSelectedCountdown} onDelete={handleDelete} />
        ))
      )}

      <CreateCountdownDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        onCreate={handleCreate} topics={topics} loading={creating} />
    </Box>
  );
}

export default function ExamCountdownPage() {
  return (<FeatureGate feature="exam_countdown" fallback={<UpgradePrompt feature="exam_countdown" />}><ExamCountdownContent /></FeatureGate>);
}
